import {Button} from "primereact/button";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {useState} from "react";
import {useTranslation} from "react-i18next";

import I18nErrorMessage, {type I18nArgs} from "../component/I18nErrorMessage";
import {IconFieldLabel} from "../component/iconFieldChildren";
import {selectError, selectErrorArgs, selectErrorHasContactLink, selectStatus} from "../redux/loginSlice";
import {tryPerformLoginAsync} from "../redux/loginThunk";
import {useRevaDispatch, useRevaSelector} from "../redux/revaHooks";
import {handlePreventDefault} from "../util/events";
import {suggestInternalLoginName} from "../util/loginNameHint";
import ForgotPasswordDialog from "./ForgotPasswordDialog";
import LoginCardHeader, {LOGIN_CARD_CLASS} from "./LoginCardHeader";

function LoginErrorMessage({enteredLoginName}: {enteredLoginName: string}) {
    const {t} = useTranslation();
    const status = useRevaSelector(selectStatus);
    const error = useRevaSelector(selectError);
    const errorArgs = useRevaSelector(selectErrorArgs);
    const errorHasContactLink = useRevaSelector(selectErrorHasContactLink);

    // Nur bei falschen Zugangsdaten: bei anderen Fehlern (fehlende Berechtigung, abgelaufene
    // Sitzung) wäre ein Hinweis auf den Anmeldenamen irreführend.
    const suggestedLoginName = error === "login.error.401" ? suggestInternalLoginName(enteredLoginName) : null;

    return (
        <I18nErrorMessage
            i18nKey={error}
            i18nArgs={errorArgs as I18nArgs}
            enabled={status === "failed"}
            hasContactLink={errorHasContactLink}
            contentAfterMessage={
                suggestedLoginName && (
                    <Message
                        id="loginNameHint"
                        severity="info"
                        className="w-full"
                        text={t("login.hint.internalLoginName", {suggestedLoginName})}
                    />
                )
            }
        />
    );
}

function LoginForm() {
    const {t} = useTranslation();

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

    const status = useRevaSelector(selectStatus);

    const dispatch = useRevaDispatch();

    function handleLoginClick() {
        if (status === "loading") {
            return;
        }
        void dispatch(tryPerformLoginAsync(username, password));
    }

    return (
        <div className="grow">
            <div className="flex items-center justify-center h-full">
                <form className={LOGIN_CARD_CLASS} onSubmit={handlePreventDefault(handleLoginClick)}>
                    <LoginCardHeader />

                    {status === "init" && (
                        <div className="text-center mb-8">
                            <i className="pi pi-spin pi-spinner" style={{fontSize: "10rem"}}></i>
                        </div>
                    )}

                    {status !== "init" && (
                        <div className="grid grid-cols-12 formgrid">
                            <div className="field col-span-12 mt-4">
                                <IconField iconPosition="left" className="p-float-label">
                                    <InputIcon className="pi pi-user" />
                                    <InputText
                                        id="inputLoginName"
                                        value={username}
                                        className="w-full"
                                        onChange={(e) => setUsername(e.target.value)}
                                        type="text"
                                        disabled={status === "loading"}
                                    />
                                    <IconFieldLabel htmlFor="inputLoginName" className="block font-medium">
                                        {t("login.username")}
                                    </IconFieldLabel>
                                </IconField>
                            </div>
                            <div className="field col-span-12 mt-4">
                                <IconField iconPosition="left" className="p-float-label">
                                    <InputIcon className="pi pi-lock" />
                                    <InputText
                                        id="inputPassword"
                                        value={password}
                                        className="w-full"
                                        onChange={(e) => setPassword(e.target.value)}
                                        type="password"
                                        disabled={status === "loading"}
                                    />
                                    <IconFieldLabel htmlFor="inputPassword" className="block font-medium">
                                        {t("login.password")}
                                    </IconFieldLabel>
                                </IconField>
                            </div>
                            <div className="field col-span-12 mt-2 text-right">
                                <a
                                    id="forgotPasswordLink"
                                    href="#"
                                    onClick={handlePreventDefault(() => setForgotPasswordOpen(true))}
                                >
                                    {t("passwordReset.linkLabel")}
                                </a>
                            </div>
                            <div className="field col-span-12 mt-8">
                                <Button
                                    id="loginButton"
                                    className="w-full"
                                    disabled={status === "loading"}
                                    label={status === "loading" ? t("login.loggingIn") : t("login.login")}
                                    icon={status === "loading" ? "pi pi-spin pi-spinner" : "pi pi-user"}
                                />
                            </div>
                            <div className="field col-span-12 mt-4 !mb-0">
                                <LoginErrorMessage enteredLoginName={username} />
                            </div>
                        </div>
                    )}
                </form>
            </div>
            <ForgotPasswordDialog visible={forgotPasswordOpen} onHide={() => setForgotPasswordOpen(false)} />
        </div>
    );
}

export default LoginForm;
